import { Link } from "react-router-dom";
import { Check, Circle, ChevronRight, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { groupFields, formatDateTime } from "@/lib/format";
import { toneVar } from "@/components/common/Badges";

/* ---------------------------------------------------------------- Page scaffolding */

export const Breadcrumbs = ({ items = [] }) => (
  <nav aria-label="Brødsmuler" className="mb-3 flex flex-wrap items-center gap-1 text-[13px] text-muted-foreground" data-testid="breadcrumbs">
    {items.map((it, i) => (
      <span key={i} className="inline-flex items-center gap-1">
        {i > 0 && <ChevronRight className="h-3.5 w-3.5 opacity-60" />}
        {it.to ? (
          <Link to={it.to} className="rounded px-1 py-0.5 transition-colors hover:bg-secondary hover:text-foreground" data-testid={it.testId}>{it.label}</Link>
        ) : (
          <span className="px-1 text-foreground">{it.label}</span>
        )}
      </span>
    ))}
  </nav>
);

/** One title per page. Meta is a short 13px line (date, count). No descriptions by default. */
export const PageHeader = ({ title, meta, actions, children, className = "", eyebrow }) => (
  <div className={`mb-6 flex flex-col gap-3 md:flex-row md:items-end md:justify-between ${className}`}>
    <div className="min-w-0">
      {eyebrow && <p className="t-meta mb-1.5" data-testid="page-eyebrow">{eyebrow}</p>}
      <h1 className="h-page" data-testid="page-title">{title}</h1>
      {meta && <p className="t-meta mt-1.5">{meta}</p>}
      {children}
    </div>
    {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
  </div>
);

/** Boxless section: a 17px title, a thin divider, then content. */
export const Section = ({ title, children, right, testId, className = "", description, as: Tag = "section" }) => (
  <Tag className={className} data-testid={testId}>
    {(title || right) && (
      <header className="mb-3 flex items-end justify-between gap-3 border-b pb-2">
        <div className="min-w-0">
          {title && <h2 className="h-section">{title}</h2>}
          {description && <p className="t-meta mt-0.5">{description}</p>}
        </div>
        {right && <div className="shrink-0 text-[13px] text-muted-foreground">{right}</div>}
      </header>
    )}
    {children}
  </Tag>
);

/**
 * A bounded surface for one job (handling a customer message, an open change, a release).
 * Head carries the title/meta and the primary action; body is the work; foot is for secondary actions.
 */
export const WorkSurface = ({ eyebrow, title, meta, actions, children, footer, testId, className = "" }) => (
  <section className={`work-surface ${className}`} data-testid={testId}>
    <div className="ws-head">
      <div className="min-w-0">
        {eyebrow && <p className="t-label">{eyebrow}</p>}
        {title && <h2 className="mt-0.5 text-[18px] font-semibold leading-snug tracking-tight">{title}</h2>}
        {meta && <p className="t-meta mt-1">{meta}</p>}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">{actions}</div>}
    </div>
    <div className="ws-body">{children}</div>
    {footer && <div className="ws-foot">{footer}</div>}
  </section>
);

/** A concrete action written as a verb, with an arrow. Neutral text; blue only on hover. */
export const ActionLink = ({ to, onClick, children, testId, className = "", target }) => {
  const inner = <>{children} <ArrowRight /></>;
  if (to) return <Link to={to} target={target} onClick={onClick} className={`action-link ${className}`} data-testid={testId}>{inner}</Link>;
  return <button type="button" onClick={onClick} className={`action-link ${className}`} data-testid={testId}>{inner}</button>;
};

export const Count = ({ n, className = "" }) => <span className={`tabular text-[13px] text-muted-foreground ${className}`}>{n}</span>;

/* ---------------------------------------------------------------- States */

export const LoadingBlock = ({ rows = 4 }) => (
  <div className="space-y-3" data-testid="loading-block" aria-busy="true" aria-live="polite">
    <div className="skeleton h-7 w-56" />
    <div className="skeleton h-4 w-80 max-w-full" />
    <div className="space-y-2 pt-3">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="skeleton h-12 w-full" />
      ))}
    </div>
  </div>
);

export const ErrorBlock = ({ message, onRetry }) => (
  <div className="callout" style={{ "--tone": toneVar("rest") }} data-testid="error-block" role="alert">
    <p className="callout-title">Kunne ikke hente data</p>
    <p className="callout-body text-muted-foreground">{message || "Prøv igjen om et øyeblikk."}</p>
    {onRetry && <Button variant="outline" size="sm" className="mt-2" onClick={onRetry} data-testid="retry-button">Prøv igjen</Button>}
  </div>
);

export const EmptyState = ({ title, description, action, className = "" }) => (
  <div className={`rounded-lg border border-dashed px-6 py-10 text-center ${className}`} data-testid="empty-state">
    <p className="text-[15px] font-medium">{title}</p>
    {description && <p className="t-meta mx-auto mt-1 max-w-sm">{description}</p>}
    {action && <div className="mt-3 flex justify-center">{action}</div>}
  </div>
);

/* ---------------------------------------------------------------- Callout */

/** Tone line + text. No pastel boxes. */
export const Callout = ({ tone = "neutral", title, children, action, testId, className = "" }) => (
  <div className={`callout ${className}`} style={{ "--tone": toneVar(tone) }} data-testid={testId}>
    {title && <p className="callout-title">{title}</p>}
    {children && <div className="callout-body">{children}</div>}
    {action && <div className="mt-2 flex flex-wrap gap-2">{action}</div>}
  </div>
);

/* ---------------------------------------------------------------- Domain blocks */

/** Next action as plain readable text (handling = actionable, venter = waiting). */
export const NextActionText = ({ action, testId = "next-action", className = "" }) => {
  if (!action) return null;
  const k = action.kind;
  const dotTone = k === "blokker" ? "rest" : k === "venter" ? "waiting" : null;
  return (
    <span className={`inline-flex items-start gap-2 text-[14px] leading-snug ${k === "handling" || k === "blokker" ? "text-foreground" : "text-muted-foreground"} ${className}`} data-testid={testId}>
      {dotTone && <span className="dot mt-[7px]" style={{ background: `hsl(${toneVar(dotTone)})` }} aria-hidden="true" />}
      <span>{action.label}</span>
    </span>
  );
};
export const NextActionChip = NextActionText;

/** Prerequisites: shows what is missing, not the satisfied-condition label. */
export const PrereqList = ({ items = [], compact = false }) => (
  <ul className={compact ? "space-y-1" : "divide-y"} data-testid="prerequisite-list">
    {items.map((p) => (
      <li key={p.key} className={`flex items-start gap-2.5 ${compact ? "py-0.5" : "py-2"}`} data-testid={`prereq-${p.key}`}>
        {p.ok ? (
          <Check className="mt-[3px] h-4 w-4 shrink-0" style={{ color: "hsl(var(--state-released))" }} aria-label="På plass" />
        ) : (
          <Circle className="mt-[4px] h-3.5 w-3.5 shrink-0" style={{ color: "hsl(var(--state-waiting))" }} aria-label="Mangler" />
        )}
        <div className="min-w-0">
          <span className={`text-[14.5px] ${p.ok ? "text-foreground" : "font-medium text-foreground"}`}>{p.ok ? p.label : p.missing || p.label}</span>
          {!compact && p.ok && p.detail && <span className="block t-meta">{p.detail}</span>}
        </div>
      </li>
    ))}
  </ul>
);

export const SpecTable = ({ fields = [], highlightKeys = [], testId = "spec-table" }) => {
  const groups = groupFields(fields);
  if (!fields.length) return <p className="t-meta">Ingen spesifikasjon registrert.</p>;
  return (
    <div className="space-y-5" data-testid={testId}>
      {groups.map(([group, items]) => (
        <div key={group}>
          <p className="t-label mb-1">{group}</p>
          <dl className="divide-y border-t">
            {items.map((f) => {
              const hl = highlightKeys.includes(f.key);
              return (
                <div key={f.key} className={`grid grid-cols-[minmax(120px,36%)_1fr] gap-3 py-2 ${hl ? "field-changed pl-2.5" : ""}`} data-testid={`spec-field-${f.key}`}>
                  <dt className="t-meta pt-0.5">{f.label}</dt>
                  <dd className="text-[15px]">{f.value}{hl && <span className="ml-2 text-[12px] font-medium" style={{ color: "hsl(var(--state-waiting))" }}>endret</span>}</dd>
                </div>
              );
            })}
          </dl>
        </div>
      ))}
    </div>
  );
};

export const SpecDiffList = ({ diff = [], testId = "spec-diff-view" }) => {
  if (!diff.length) return <p className="t-meta" data-testid={testId}>Ingen feltendringer.</p>;
  return (
    <dl className="divide-y border-t" data-testid={testId}>
      {diff.map((d) => (
        <div key={d.key} className="grid grid-cols-[minmax(120px,36%)_1fr] gap-3 py-2 text-[15px]" data-testid={`spec-diff-${d.key}`}>
          <dt className="t-meta pt-0.5">{d.label}</dt>
          <dd className="flex flex-wrap items-center gap-2">
            {d.from && <span className="diff-from">{d.from}</span>}
            {d.from && d.to && <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />}
            {d.to ? <span className="diff-to">{d.to}</span> : <span className="t-meta">(fjernet)</span>}
          </dd>
        </div>
      ))}
    </dl>
  );
};

export const EVENT_LABELS = {
  melding: "Melding", endring: "Endring", vurdering: "Vurdering", revisjon: "Revisjon", tegning: "Tegning", godkjenning: "Godkjenning", frigivelse: "Frigivelse", plan: "Plan",
  montering: "Montering", service: "Service", dokument: "Dokument", fase: "Fase", oppmaling: "Oppmåling", produksjon: "Produksjon", kontroll: "Kontroll",
};

export const Timeline = ({ events = [], limit, testId = "delivery-history-timeline" }) => {
  const sorted = [...events].sort((a, b) => (b.at || "").localeCompare(a.at || ""));
  const list = limit ? sorted.slice(0, limit) : sorted;
  if (!list.length) return <p className="t-meta">Ingen hendelser.</p>;
  return (
    <ol className="divide-y" data-testid={testId}>
      {list.map((e) => (
        <li key={e.id} className="grid grid-cols-1 gap-x-4 gap-y-0.5 py-2.5 sm:grid-cols-[112px_1fr]" data-testid="history-event-row">
          <span className="t-meta tabular pt-0.5">{formatDateTime(e.at)}</span>
          <div className="min-w-0">
            <p className="text-[14.5px] font-medium leading-snug">{e.title}</p>
            {e.detail && <p className="mt-0.5 text-[14px] leading-relaxed text-muted-foreground">{e.detail}</p>}
            <p className="t-meta mt-0.5">{e.actor} · {EVENT_LABELS[e.type] || e.type}</p>
          </div>
        </li>
      ))}
    </ol>
  );
};

export const DeliveryLink = ({ id, number, name, className = "" }) => (
  <Link to={`/leveranser/${id}`} className={`group inline-flex min-w-0 items-baseline gap-2 ${className}`} data-testid={`delivery-link-${number}`}>
    <span className="truncate text-[15px] font-semibold transition-colors group-hover:text-primary">{name}</span>
    <span className="t-meta tabular">{number}</span>
  </Link>
);

export const FictitiousNote = ({ children = "Fiktive eksempelverdier" }) => (
  <p className="text-[12px] text-muted-foreground" data-testid="fictitious-note">{children}</p>
);

export const KV = ({ k, v, className = "" }) => (
  <div className={`flex flex-col gap-0.5 ${className}`}><dt className="t-meta">{k}</dt><dd className="text-[14.5px]">{v ?? "–"}</dd></div>
);

/** Compact definition rows: label left, value right, thin dividers. */
export const KVList = ({ items = [], testId }) => (
  <dl className="divide-y border-t" data-testid={testId}>
    {items.filter(Boolean).map(([k, v]) => (
      <div key={k} className="grid grid-cols-[minmax(110px,38%)_1fr] gap-3 py-2">
        <dt className="t-meta pt-0.5">{k}</dt>
        <dd className="text-[14.5px]">{v ?? "–"}</dd>
      </div>
    ))}
  </dl>
);
